// Direct localStorage clearing to avoid Zustand caching issues
import { useAuthStore, forceResetAuthState } from './auth.store';

/**
 * Tab tracker: clears secure storage (including auth token)
 * when the user closes the last open tab of the app.
 *
 * Strategy:
 *  - Each tab registers itself with a unique ID in a registry stored
 *    in localStorage, along with a "last seen" timestamp (heartbeat).
 *  - A heartbeat refreshes the timestamp every few seconds.
 *  - On load, any tab whose heartbeat is older than STALE_MS is
 *    considered dead (e.g. browser crash) and removed.
 *  - On unload, the tab removes itself. If the registry becomes
 *    empty, secure storage is wiped (unless it's a page refresh).
 *  - Page refresh is detected via sessionStorage and does NOT clear storage.
 */
const REGISTRY_KEY = 'volovolo:open-tabs';
const REFRESH_FLAG_KEY = 'volovolo:page-refresh';
const LAST_CLOSE_KEY = 'volovolo:last-close';
const CLEARED_FLAG_KEY = 'volovolo:session-cleared';
const TAB_ALIVE_KEY = 'volovolo:tab-alive';
const HEARTBEAT_MS = 3000;
const STALE_MS = 10000;

/**
 * Check if this page load is a refresh (not a fresh navigation)
 * Uses PerformanceNavigationTiming API as primary method,
 * falls back to sessionStorage flag
 */
function isPageRefresh(): boolean {
  // Primary method: Use PerformanceNavigationTiming API
  try {
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navEntries.length > 0) {
      const navType = navEntries[0].type;
      console.log('[TabTracker] Navigation type:', navType);
      // 'reload' = F5/refresh, 'navigate' = new page/restored tab, 'back_forward' = history
      if (navType === 'reload') {
        // Clean up the flag since we detected refresh via API
        try { sessionStorage.removeItem(REFRESH_FLAG_KEY); } catch {}
        return true;
      }
      // If it's 'navigate' or 'back_forward', it's NOT a refresh
      // Clean up any stale flag
      try { sessionStorage.removeItem(REFRESH_FLAG_KEY); } catch {}
      return false;
    }
  } catch {
    // API not available, fall back to sessionStorage
  }

  // Fallback: Check sessionStorage flag
  try {
    const refreshFlag = sessionStorage.getItem(REFRESH_FLAG_KEY);
    if (refreshFlag) {
      sessionStorage.removeItem(REFRESH_FLAG_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/**
 * Mark that we're potentially doing a refresh
 */
function markPotentialRefresh(): void {
  try {
    sessionStorage.setItem(REFRESH_FLAG_KEY, 'true');
  } catch {
    // ignore
  }
}

/**
 * Mark that the last tab was closed (storage should be cleared)
 */
function markLastTabClosed(): void {
  try {
    localStorage.setItem(LAST_CLOSE_KEY, Date.now().toString());
  } catch {
    // ignore
  }
}

/**
 * Check if we should clear storage based on last tab close marker
 */
function shouldClearFromLastClose(): boolean {
  try {
    const lastClose = localStorage.getItem(LAST_CLOSE_KEY);
    if (lastClose) {
      localStorage.removeItem(LAST_CLOSE_KEY);
      // If last close was within the last 5 minutes, clear storage
      const closeTime = parseInt(lastClose, 10);
      if (Date.now() - closeTime < 5 * 60 * 1000) {
        return true;
      }
    }
  } catch {
    // ignore
  }
  return false;
}

type Registry = Record<string, number>;

function readRegistry(): Registry {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Registry) : {};
  } catch {
    return {};
  }
}

function writeRegistry(reg: Registry): void {
  try {
    if (Object.keys(reg).length === 0) {
      localStorage.removeItem(REGISTRY_KEY);
    } else {
      localStorage.setItem(REGISTRY_KEY, JSON.stringify(reg));
    }
  } catch {
    // ignore
  }
}

function pruneStale(reg: Registry): Registry {
  const now = Date.now();
  const fresh: Registry = {};
  for (const [id, ts] of Object.entries(reg)) {
    if (now - ts < STALE_MS) fresh[id] = ts;
  }
  return fresh;
}

export function initTabTracker(): void {
  if (typeof window === 'undefined') return;

  // Check if this is a page refresh (sessionStorage survives refresh)
  const wasRefresh = isPageRefresh();

  // sessionStorage survives navigations plein-document of the same tab, but NOT
  // tab closure → a tab already "alive" should never be treated as new.
  const tabAlive = (() => { try { return sessionStorage.getItem(TAB_ALIVE_KEY) === '1'; } catch { return false; } })();
  try { sessionStorage.setItem(TAB_ALIVE_KEY, '1'); } catch { /* ignore */ }

  // Check registry BEFORE registering this tab
  const rawRegistry = readRegistry();
  const registryBeforeUs = pruneStale(rawRegistry);
  const isRegistryEmpty = Object.keys(registryBeforeUs).length === 0;

  // Check if last tab was closed and marked for storage clearing
  const hasLastCloseMarker = shouldClearFromLastClose();

  // Debug logging
  console.log('[TabTracker] Init - wasRefresh:', wasRefresh);
  console.log('[TabTracker] Init - rawRegistry:', rawRegistry);
  console.log('[TabTracker] Init - afterPrune:', registryBeforeUs);
  console.log('[TabTracker] Init - isRegistryEmpty:', isRegistryEmpty);
  console.log('[TabTracker] Init - hasLastCloseMarker:', hasLastCloseMarker);
  console.log('[TabTracker] Init - lastCloseValue:', localStorage.getItem(LAST_CLOSE_KEY));

  // Clear storage if:
  // 1. It's NOT a same-tab continuation (refresh OR pre-existing sessionStorage marker) AND
  // 2. Either the last-close marker exists OR the registry is empty (all tabs stale/closed)
  const isSameTabContinuation = wasRefresh || tabAlive;
  const shouldClear = !isSameTabContinuation && (hasLastCloseMarker || isRegistryEmpty);

  console.log('[TabTracker] shouldClear:', shouldClear);

  // Check if we recently cleared (within last 5 seconds) to prevent infinite redirect loop
  const clearedAtStr = sessionStorage.getItem(CLEARED_FLAG_KEY);
  const clearedAt = clearedAtStr ? parseInt(clearedAtStr, 10) : 0;
  const timeSinceClear = Date.now() - clearedAt;
  const recentlyCleared = clearedAt > 0 && timeSinceClear < 5000; // 5 seconds

  console.log('[TabTracker] clearedAt:', clearedAt, 'timeSinceClear:', timeSinceClear, 'recentlyCleared:', recentlyCleared);

  // Public paths that should not trigger redirect to login
  const publicPaths = [
    '/login',
    '/signup',
    '/forgot-password',
    '/forget/password',
    '/resend-activation',
    '/success/account/activation',
    '/failed/account/activation',
    '/request/seller/delete',
    '/request/seller/confirm',
    '/privacy-policy',
    '/cgv',
    '/terms-of-use',
  ];
  const currentPath = window.location.pathname;
  const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));

  if (shouldClear && !recentlyCleared) {
    console.log('[TabTracker] >>> CLEARING STORAGE <<<');
    try {
      // Mark when we cleared (timestamp) to prevent infinite loop for 5 seconds
      sessionStorage.setItem(CLEARED_FLAG_KEY, Date.now().toString());

      // Clear all volovolo localStorage keys directly
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('volovolo')) {
          keysToRemove.push(key);
        }
      }
      console.log('[TabTracker] Removing localStorage keys:', keysToRemove);
      keysToRemove.forEach(key => localStorage.removeItem(key));

      console.log('[TabTracker] localStorage after clear:', Object.keys(localStorage));

      // Reset the auth store state (don't rehydrate - we want it cleared)
      forceResetAuthState();

      // Only redirect to login if NOT already on a public page
      if (!isPublicPage) {
        window.location.href = '/login';
        return;
      }
    } catch (e) {
      console.error('[TabTracker] Clear failed:', e);
    }
  }

  // Clear the flag once conditions are normal
  if (!shouldClear && clearedAt > 0) {
    sessionStorage.removeItem(CLEARED_FLAG_KEY);
  }

  // Manually rehydrate the auth store since we're NOT clearing
  // This is required because auth.store has skipHydration: true
  console.log('[TabTracker] Rehydrating auth store...');
  useAuthStore.persist.rehydrate();

  const tabId =
    (crypto as Crypto & { randomUUID?: () => string }).randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Register this tab
  registryBeforeUs[tabId] = Date.now();
  writeRegistry(registryBeforeUs);

  // Heartbeat to keep this tab alive in the registry
  const heartbeat = window.setInterval(() => {
    const reg = pruneStale(readRegistry());
    reg[tabId] = Date.now();
    writeRegistry(reg);
  }, HEARTBEAT_MS);

  const handleUnload = () => {
    // Mark potential refresh FIRST (sessionStorage)
    markPotentialRefresh();

    window.clearInterval(heartbeat);
    const reg = pruneStale(readRegistry());
    delete reg[tabId];

    // If this was the last tab, mark for storage clearing
    if (Object.keys(reg).length === 0) {
      markLastTabClosed();
    }

    writeRegistry(reg);
  };

  window.addEventListener('beforeunload', handleUnload);
  window.addEventListener('pagehide', handleUnload);
}
